import { PLACE_ID_PATTERN, type PlaceCoords } from '@bandido/shared/types';

import { countryName } from './destinations';
import { formatMinutes } from './format';
import { CatalogEntry, FerrySearchPayload, PricedSearchResult } from './queries';
import { formatDuration, formatLocalDate } from './rail';
import { PlaceRef } from './places';
import { routeMapEndpoints } from './routeMap';
import type { RouteMapPoint } from './routeMap';

/**
 * Helpers del vertical de ferrys.
 *
 * Los horarios son hora LOCAL del puerto sin offset (mismo criterio que
 * trenes: convertirlos a UTC sería inventar información), así que se formatean
 * como texto, nunca con `new Date()` + zona del browser.
 *
 * Los datos de pasajero que exige el operador NO se resuelven acá: los declara
 * la oferta, igual que en los otros ocho verticales, y los interpreta
 * `passengerFields.ts`. Ferry tenía su propio mapeo de variantes de proveedor
 * ("PassportNumber", "DateOfBirth") porque el contrato viejo mandaba los
 * nombres crudos del operador; el nuevo los manda normalizados.
 */

export type FerryOffer = PricedSearchResult & {
  operator?: string;
  fromPort?: string;
  toPort?: string;
  departure?: string;
  arrival?: string;
  returnDeparture?: string;
  returnArrival?: string;
  durationMinutes?: number;
  /** La vuelta puede durar distinto que la ida (rutas y buques distintos). */
  returnDurationMinutes?: number;
  vesselName?: string;
  /** Logo de la naviera, servido por el hub (URL ya validada por el backend). */
  operatorLogoUrl?: string;
  /** El trayecto incluye un tramo en BUS (el detalle va en providerMessages). */
  busTransferIncluded?: boolean;
  /** Avisos del operador sobre la travesía (texto suyo, se muestra tal cual). */
  providerMessages?: string[];
  /** Letra chica de la tarifa ("This ticket is non-refundable"). */
  ticketConditions?: string;
  /** Antelación de check-in en el puerto, en minutos. */
  checkInMinutes?: number;
  fromPortAddress?: string;
  toPortAddress?: string;
  /**
   * La travesía EXIGE que cada pasajero tenga plaza asignada: el operador
   * rechaza el booking sin ella (contrato de rutero), así que el hold se gatea
   * a cubrir a todos cuando hay plazas publicadas. Sin este flag, elegir es
   * opcional y no se valida cantidad (el contrato no dice más). Viaja en la
   * OFERTA porque la lista de acomodaciones llega vacía en el search; es el
   * respaldo cuando el endpoint de opciones no repite el flag.
   */
  accommodationCompulsory?: boolean;
};

/** Una oferta con tramo de vuelta cotiza el TOTAL ida+vuelta. */
export function isRoundTrip(offer: FerryOffer): boolean {
  return Boolean(offer.returnDeparture);
}

/**
 * Duración de un tramo. El hub publica `durationMinutes` SOLO del tramo de ida,
 * así que para la vuelta se deriva de sus horas. Es una aproximación: si los dos
 * puertos están en husos distintos, la resta de horas locales se desvía por esa
 * diferencia (pendiente: que el hub exponga la duración de cada tramo).
 */
export function legDuration(
  minutes: number | undefined,
  departure: string | undefined,
  arrival: string | undefined,
): string | undefined {
  const published = formatMinutes(minutes);
  if (published) return published;
  return formatDuration(departure, arrival) === '—'
    ? undefined
    : formatDuration(departure, arrival);
}

/** Un tramo de la travesía, listo para el bloque de horarios. */
export interface FerryLeg {
  departure?: string;
  arrival?: string;
  fromPort?: string;
  toPort?: string;
  /**
   * Día de salida ya formateado (`''` si el proveedor no lo mandó). Viaja EN
   * el tramo y no en una rama del render: sin la fecha, un horario suelto no
   * es un itinerario, y es el dato que más fácil se cae cuando el layout
   * distingue ida y vuelta de un tramo único.
   */
  date: string;
  /** Ya formateada; `undefined` si no se puede saber (ver `legDuration`). */
  duration?: string;
  isReturn: boolean;
}

/**
 * Los tramos que hay que dibujar. Una oferta de ida y vuelta cotiza el TOTAL
 * de los dos tramos en una sola oferta, así que la card muestra ambos: mostrar
 * solo la ida dejaría al asesor sin la hora de vuelta que ya está cotizada.
 *
 * El hub NO repite los puertos para la vuelta —los recorre al revés—, así que
 * se invierten acá y no en cada card.
 */
export function ferryLegs(offer: FerryOffer): FerryLeg[] {
  const outbound: FerryLeg = {
    departure: offer.departure,
    arrival: offer.arrival,
    fromPort: offer.fromPort,
    toPort: offer.toPort,
    date: formatLocalDate(offer.departure),
    duration: legDuration(offer.durationMinutes, offer.departure, offer.arrival),
    isReturn: false,
  };

  if (!isRoundTrip(offer)) return [outbound];

  return [
    outbound,
    {
      departure: offer.returnDeparture,
      arrival: offer.returnArrival,
      fromPort: offer.toPort,
      toPort: offer.fromPort,
      date: formatLocalDate(offer.returnDeparture),
      duration: legDuration(
        offer.returnDurationMinutes,
        offer.returnDeparture,
        offer.returnArrival,
      ),
      isReturn: true,
    },
  ];
}

/** Etiqueta del vehículo con sus dimensiones, que es lo que define la tarifa. */
export function vehicleLabel(entry: CatalogEntry): string {
  const height = entry.meta?.heightCm;
  const length = entry.meta?.lengthCm;
  if (typeof height === 'number' && typeof length === 'number') {
    return `${entry.name} · ${(length / 100).toFixed(2)}m × ${(height / 100).toFixed(2)}m`;
  }
  return entry.name;
}

/**
 * Puerto con su país, para distinguir homónimos entre costas. El país se
 * muestra traducido al idioma del usuario (el proveedor solo manda el ISO-2).
 */
export function portLabel(entry: CatalogEntry, language: string): string {
  const country = countryName(entry.countryCode, language) ?? entry.countryCode;
  return country ? `${entry.name}, ${country}` : entry.name;
}

function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

/**
 * Matchea el texto tipeado contra el nombre del proveedor, el país traducido y
 * el código (ESBCN), ignorando acentos — así "coruna", "españa" y "ESBCN"
 * encuentran su puerto.
 */
export function matchesPort(
  entry: CatalogEntry,
  query: string,
  language: string,
): boolean {
  const needle = normalize(query);
  if (!needle) return true;
  return [
    entry.name,
    entry.id,
    entry.countryCode ?? '',
    countryName(entry.countryCode, language) ?? '',
  ].some((candidate) => normalize(candidate).includes(needle));
}

export type FerrySort = 'departure' | 'price' | 'duration';

export function sortFerryOffers(
  offers: FerryOffer[],
  sort: FerrySort,
): FerryOffer[] {
  return [...offers].sort((a, b) => {
    if (sort === 'price') {
      return a.pricing.final.amountMinor - b.pricing.final.amountMinor;
    }
    if (sort === 'duration') {
      return (a.durationMinutes ?? Infinity) - (b.durationMinutes ?? Infinity);
    }
    return (a.departure ?? '').localeCompare(b.departure ?? '');
  });
}

/** Datos de la ruta que publica el proveedor, dentro de `meta`. */
export interface FerryRouteMeta {
  toCode?: string;
  toName?: string;
  operator?: string;
  footPassenger?: boolean;
  vehicle?: boolean;
  allowPets?: boolean;
}

export function routeMeta(entry: CatalogEntry): FerryRouteMeta {
  return (entry.meta ?? {}) as FerryRouteMeta;
}

/**
 * Los destinos alcanzables, a partir de las rutas. Un par de puertos puede
 * tener VARIAS rutas —una por naviera—, así que se dedupean por puerto de
 * llegada: Barcelona devuelve 28 rutas pero 14 destinos, y ofrecer el mismo
 * destino catorce veces es ruido.
 *
 * Las capacidades se acumulan con OR: si alguna naviera del par lleva pasajero
 * a pie, el destino admite pasajero a pie.
 */
export function routesToDestinations(routes: CatalogEntry[]): CatalogEntry[] {
  const byPort = new Map<string, CatalogEntry>();

  for (const route of routes) {
    const meta = routeMeta(route);
    if (!meta.toCode) continue;
    const existing = byPort.get(meta.toCode);
    const previous = existing ? routeMeta(existing) : undefined;
    byPort.set(meta.toCode, {
      id: meta.toCode,
      name: meta.toName ?? meta.toCode,
      meta: {
        footPassenger: Boolean(previous?.footPassenger || meta.footPassenger),
        vehicle: Boolean(previous?.vehicle || meta.vehicle),
        allowPets: Boolean(previous?.allowPets || meta.allowPets),
      },
    });
  }

  return [...byPort.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Lugar (`plc_*`) reconstruido desde la URL. Una referencia no canónica se
 * descarta y nunca se interpreta como código de puerto.
 *
 * A diferencia de cars, ferry NO necesita `country`: `FerrySearchPayload`
 * no lo pide. `kind`/`breadcrumb` quedan con el `name` como placeholder,
 * igual que en rail — ninguno de los dos se renderiza para el valor YA
 * seleccionado (ver `PlaceAutocomplete.tsx`).
 */
export function ferryPlaceFromParams(
  id: string | undefined,
  name: string | undefined,
): PlaceRef | null {
  if (!id || !PLACE_ID_PATTERN.test(id)) return null;
  const resolvedName = name ?? id;
  return { id, name: resolvedName, breadcrumb: resolvedName, kind: 'city' };
}

/**
 * ¿La búsqueda YA CORRIDA (`searchPayload`, derivado de la URL committeada)
 * es del MISMO par origen/destino que los pickers tienen elegido AHORA
 * (`fromId`/`toId`, el estado vivo)? Decide si el resultado autoritativo de
 * esa búsqueda puede hablar por el par actual — si el asesor cambió el
 * destino DESPUÉS de buscar, una búsqueda vieja no dice nada del par nuevo
 * y no puede usarse para retirar un aviso que sí es sobre el par nuevo
 * (21.23: el registro autoritativo manda, pero solo sobre lo que
 * efectivamente contestó).
 */
export function ferrySearchMatchesPair(
  payload: { fromPort: string; toPort: string } | null | undefined,
  fromId: string | undefined,
  toId: string | undefined,
): boolean {
  return Boolean(
    payload && fromId && toId && payload.fromPort === fromId && payload.toPort === toId,
  );
}

/**
 * Los dos puertos del pedido, para el mapa del recorrido (#2228). Se resuelven
 * contra las coordenadas canónicas resueltas por id. El `plc_*` del pedido
 * sigue viajando opaco a Rutero; estas coordenadas se usan solo para el mapa.
 */
export function ferryRouteMapPoints(
  payload: FerrySearchPayload | null,
  placeCoords: Record<string, PlaceCoords>,
  labels: { from?: string; to?: string } = {},
): RouteMapPoint[] {
  if (!payload) return [];
  return routeMapEndpoints(
    { label: labels.from ?? payload.fromPort, coords: placeCoords[payload.fromPort] },
    { label: labels.to ?? payload.toPort, coords: placeCoords[payload.toPort] },
  );
}
